import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useProject } from '../context/ProjectContext';
import { useConfirm } from '../context/ConfirmContext';
import { useDataVersion } from '../context/DataVersionContext';
import api from '../lib/api';
import { formatBRL } from '../lib/format';
import { Plus, Trash2, Edit2, X, GripVertical, Search, CreditCard, Layers, Merge } from 'lucide-react';
import { IconDisplay } from '../lib/icons';
import IconPicker from '../components/IconPicker';
import MoneyInput from '../components/MoneyInput';
import BotaoFechar, { useEscFecha } from '../components/BotaoFechar';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PALETTE = ['#6366f1','#3b82f6','#10b981','#ec4899','#f97316','#eab308','#8b5cf6','#06b6d4','#ef4444','#14b8a6'];

/* Recursos cujo `type` é texto livre — e que por isso derivam. Ver o
   comentário de COLECOES_COM_TIPO no backend. */
const TEM_TIPO = ['pots', 'accounts', 'entities'];

export default function ResourceListPage({ resource, title, withBalance = true, extraFields = [], linkTo = null }) {
  const { currentProject } = useProject();
  const confirm = useConfirm();
  const { bump, version } = useDataVersion();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [showTipos, setShowTipos] = useState(false);

  const carregarTipos = useCallback(() => {
    if (!currentProject || !TEM_TIPO.includes(resource)) return;
    api.get(`/projects/${currentProject.id}/tipos/${resource}`)
      .then((r) => setTipos(r.data || []))
      .catch(() => setTipos([]));
  }, [currentProject, resource]);
  useEffect(carregarTipos, [carregarTipos, version]);

  const load = () => {
    if (!currentProject) return;
    setLoading(true);
    api.get(`/projects/${currentProject.id}/${resource}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [currentProject, resource, version]);

  const save = async (data) => {
    try {
      if (editing) await api.put(`/${resource}/${editing.id}`, data);
      else await api.post(`/projects/${currentProject.id}/${resource}`, data);
      toast.success(editing ? 'Atualizado' : 'Criado');
      setShowForm(false); setEditing(null); load(); bump();
    } catch (err) {
      toast.error('Erro ao salvar', { description: err?.response?.data?.detail || err.message });
    }
  };
  const remove = async (item) => {
    const ok = await confirm({
      title: 'Excluir este item?',
      description: `${item.name}${withBalance && item.balance != null ? ` · ${formatBRL(item.balance)}` : ''}`,
      confirmText: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/${resource}/${item.id}`);
      toast.success('Excluído');
      load(); bump();
    } catch (err) {
      toast.error('Erro ao excluir', { description: err?.response?.data?.detail || err.message });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    try {
      await api.post(`/projects/${currentProject.id}/${resource}/reorder`, { ids: next.map((i) => i.id) });
    } catch {}
  };

  if (!currentProject) return <div className="text-slate-500 dark:text-slate-400">Selecione um projeto.</div>;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((it) => (it.name || '').toLowerCase().includes(q) || (it.description || '').toLowerCase().includes(q) || (it.type || '').toLowerCase().includes(q))
    : items;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">{title}</h2>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg shadow-md hover:shadow-lg" data-testid="resource-new-btn">
          <Plus className="w-4 h-4" /> Novo
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={`Buscar em ${title.toLowerCase()}...`} className="input-md pl-10 pr-10" data-testid="resource-search-input" />
          {search && (
            <button type="button" onClick={() => setSearch('')} data-testid="resource-search-clear-icon"
              aria-label="Limpar busca" title="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {search && (
          <button onClick={() => setSearch('')} data-testid="resource-search-clear"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
            <X className="w-4 h-4" /> Limpar
          </button>
        )}
        {TEM_TIPO.includes(resource) && (
          <button onClick={() => setShowTipos((v) => !v)} data-testid="resource-tipos-toggle"
            title="Ver, renomear e juntar os tipos usados"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border whitespace-nowrap ${
              showTipos ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
            <Layers className="w-4 h-4" /> Tipos
          </button>
        )}
      </div>

      {showTipos && TEM_TIPO.includes(resource) && (
        <PainelTipos pid={currentProject.id} resource={resource} tipos={tipos}
          onMudou={() => { carregarTipos(); load(); bump(); }} />
      )}

      {loading ? <div className="text-slate-500 dark:text-slate-400">Carregando...</div> : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={filtered.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((it) => (
                <SortableCard key={it.id} item={it} withBalance={withBalance}
                  onClick={() => linkTo && navigate(`${linkTo}/${it.id}/transactions`)}
                  onEdit={() => { setEditing(it); setShowForm(true); }}
                  onDelete={() => remove(it)}
                />
              ))}
              {filtered.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500 dark:text-slate-400">
                  {items.length === 0 ? 'Nenhum item cadastrado.' : 'Nenhum resultado para essa busca.'}
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {showForm && (
        <FormModal initial={editing} tipos={tipos} onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save} withBalance={withBalance} extraFields={extraFields} title={title} />
      )}
    </div>
  );
}

/**
 * Ver, renomear e juntar os tipos usados.
 *
 * Renomear para um nome que já existe FUNDE os dois — é assim que se resolve
 * "1 - Uso Cotidiano" e "Uso Cotidiano" convivendo, sem precisar abrir item
 * por item. A tela avisa quando a operação vai fundir, para não ser surpresa.
 */
function PainelTipos({ pid, resource, tipos, onMudou }) {
  const [editandoTipo, setEditandoTipo] = useState(null);
  const [novoNome, setNovoNome] = useState('');
  const [salvando, setSalvando] = useState(false);

  const nomes = tipos.map((t) => t.tipo).filter(Boolean);
  const vaiFundir = editandoTipo !== null && novoNome.trim() !== ''
    && novoNome.trim() !== editandoTipo && nomes.includes(novoNome.trim());

  const renomear = async () => {
    setSalvando(true);
    try {
      const { data } = await api.post(`/projects/${pid}/tipos/${resource}/renomear`,
        { de: editandoTipo, para: novoNome.trim() });
      toast.success(data.fundiu ? 'Tipos juntados' : 'Tipo renomeado',
        { description: `${data.alterados} item(ns) atualizado(s)` });
      setEditandoTipo(null); setNovoNome('');
      onMudou();
    } catch (err) {
      toast.error('Erro ao renomear', { description: err?.response?.data?.detail || err.message });
    } finally { setSalvando(false); }
  };

  return (
    <div className="card p-5" data-testid="painel-tipos">
      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2">
        <Layers className="w-4 h-4 text-indigo-600" /> Tipos em uso
      </h3>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        Renomear para um nome que já existe <strong>junta</strong> os dois. É assim que se
        resolve o mesmo tipo escrito de duas formas.
      </p>
      <div className="space-y-2">
        {tipos.length === 0 && <div className="text-sm text-slate-400">Nenhum item cadastrado ainda.</div>}
        {tipos.map((t) => (
          <div key={t.tipo || '(vazio)'} className="flex flex-wrap items-center gap-2 py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
            {editandoTipo === t.tipo ? (
              <>
                <input autoFocus value={novoNome} onChange={(e) => setNovoNome(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') renomear(); if (e.key === 'Escape') setEditandoTipo(null); }}
                  list="tipos-existentes" data-testid="tipo-novo-nome"
                  className="input-md flex-1 min-w-[180px]" placeholder="novo nome" />
                <button onClick={renomear} disabled={salvando || !novoNome.trim()} data-testid="tipo-salvar"
                  className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1">
                  {vaiFundir && <Merge className="w-3.5 h-3.5" />}
                  {salvando ? '...' : vaiFundir ? 'Juntar' : 'Salvar'}
                </button>
                <button onClick={() => setEditandoTipo(null)}
                  className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm text-slate-600 dark:text-slate-300">
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <span className={`flex-1 min-w-0 text-sm truncate ${t.tipo ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 italic'}`}>
                  {t.tipo || '(sem tipo)'}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums whitespace-nowrap">
                  {t.quantidade} {t.quantidade === 1 ? 'item' : 'itens'}
                </span>
                <button onClick={() => { setEditandoTipo(t.tipo); setNovoNome(t.tipo || ''); }}
                  data-testid={`tipo-editar-${t.tipo || 'vazio'}`}
                  className="text-xs px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600">
                  Renomear
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      {vaiFundir && (
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2">
          <strong>"{novoNome.trim()}"</strong> já existe. Os itens de <strong>"{editandoTipo}"</strong> passarão a usá-lo.
        </p>
      )}
    </div>
  );
}

function SortableCard({ item, withBalance, onClick, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const color = item.color || '#6366f1';
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, borderColor: color };
  return (
    <div ref={setNodeRef} style={style}
      className={`group bg-white dark:bg-slate-800 rounded-2xl border-2 shadow-sm p-5 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={(e) => { if (e.target.closest('button')) return; onClick?.(); }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <button {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}
            className="cursor-grab p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="w-4 h-4" />
          </button>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md" style={{ background: item.color || '#6366f1' }}>
            <IconDisplay name={item.icon} className="w-6 h-6" />
          </div>
        </div>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={onEdit} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"><Edit2 className="w-4 h-4" /></button>
          <button onClick={onDelete} className="p-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 text-rose-500"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{item.name}</div>
      {item.type && <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">{item.type}</div>}
      {item.is_credit_card && (
        <div className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-[11px] font-medium">
          <CreditCard className="w-3 h-3" /> Cartão de crédito
        </div>
      )}
      {item.description && <div className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{item.description}</div>}
      {withBalance && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
          <div className="text-xs text-slate-500 dark:text-slate-400">Saldo atual</div>
          <div className={`text-xl font-bold ${item.balance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{formatBRL(item.balance)}</div>
        </div>
      )}
    </div>
  );
}

function FormModal({ initial, tipos = [], onClose, onSave, withBalance, extraFields, title }) {
  const [form, setForm] = useState(initial || { name: '', type: '', description: '', color: '#6366f1', icon: 'DollarSign', initial_balance: 0, is_credit_card: false });
  const upd = (k, v) => setForm({ ...form, [k]: v });
  useEscFecha(onClose);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{initial ? 'Editar' : 'Novo'} {title.slice(0,-1)}</h3>
          <BotaoFechar onClose={onClose} testid="recurso-form-fechar" />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-3">
          <Field label="Nome"><input value={form.name||''} onChange={(e)=>upd('name', e.target.value)} required className="input" /></Field>
          {/* A lista sugere o que já existe. É o que evita "Uso Cotidiano"
              nascer ao lado de "1 - Uso Cotidiano" — sem impedir um tipo novo. */}
          <Field label="Tipo">
            <input value={form.type||''} onChange={(e)=>upd('type', e.target.value)}
              list="tipos-existentes" placeholder="digite ou escolha um existente" className="input" />
            <datalist id="tipos-existentes">
              {tipos.filter((t) => t.tipo).map((t) => <option key={t.tipo} value={t.tipo} />)}
            </datalist>
          </Field>
          {extraFields.includes('email') && <Field label="Email"><input value={form.email||''} onChange={(e)=>upd('email', e.target.value)} className="input" /></Field>}
          {extraFields.includes('phone') && <Field label="Telefone"><input value={form.phone||''} onChange={(e)=>upd('phone', e.target.value)} className="input" /></Field>}
          <Field label="Descrição"><input value={form.description||''} onChange={(e)=>upd('description', e.target.value)} className="input" /></Field>
          {withBalance && (
            <Field label="Saldo inicial">
              <MoneyInput value={form.initial_balance||0} onChange={(v)=>upd('initial_balance', v)} className="input" placeholder="0,00" />
            </Field>
          )}
          {extraFields.includes('credit_card') && (
            <label className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40">
              <input type="checkbox" checked={!!form.is_credit_card}
                onChange={(e) => upd('is_credit_card', e.target.checked)}
                data-testid="pot-is-credit-card"
                className="mt-0.5 w-4 h-4 accent-indigo-600 shrink-0" />
              <span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Este pote representa um cartão de crédito</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  A compra sai do pote de orçamento e entra aqui; o pagamento da fatura sai daqui.
                  O saldo deste pote passa a ser a fatura em aberto, e o gasto deixa de ser contado duas vezes.
                </span>
              </span>
            </label>
          )}
          <Field label="Cor">
            <div className="flex gap-2 flex-wrap">
              {PALETTE.map((c) => (
                <button type="button" key={c} onClick={() => upd('color', c)}
                  className={`w-8 h-8 rounded-lg border-2 ${form.color===c?'border-slate-800 dark:border-slate-100':'border-transparent'}`}
                  style={{ background: c }} />
              ))}
            </div>
          </Field>
          <Field label="Ícone">
            <IconPicker value={form.icon} onChange={(v) => upd('icon', v)} />
          </Field>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">Cancelar</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg shadow-md">Salvar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
